import type { Point } from "highcharts"
import ReactDOMServer from "react-dom/server"
import { SERIE_STACK } from "./index"
import { ChartTooltip } from "./TooltipComponent"
import { isOverlayPoint } from "./utils/isOverlayPoint"

/** @deprecated */
export type TooltipOptions = {
  sort: boolean
  showTotal: boolean
  headerOverride?: string
  isLowerValueBetter?: boolean
  /** How to aggregate point values for the bottom row. Defaults to "sum". */
  aggregation?: "sum" | "median"
  /** Label for the aggregation row. Defaults to "Total". */
  aggregationLabel?: string
}

function median(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!
}

/** @deprecated */
export function formatTooltip(
  formatterContext: Highcharts.TooltipFormatterContextObject,
  tooltip: Highcharts.Tooltip,
  tooltipOptions: TooltipOptions = { sort: false, showTotal: true },
): Array<string> | string {
  let header
  let footerTimestamp
  const aggregationType = tooltipOptions.aggregation ?? "sum"
  const aggregationLabel = tooltipOptions.aggregationLabel ?? "Total"
  let aggregateValue = 0

  // Not sure why we do this, but it was here before
  tooltipOptions.sort = true

  if (tooltipOptions.sort && formatterContext.points) {
    formatterContext.points?.sort((a, b) => {
      // Overlay series are a different reading on a different axis, so they
      // are pinned above the stack instead of ranked against it. Left in value
      // order they would sort below every band, and the panel draws only its
      // first ten rows — the row would simply not be there.
      const overlayOrder = Number(isOverlayPoint(b)) - Number(isOverlayPoint(a))
      if (overlayOrder !== 0) return overlayOrder
      const result = tooltipOptions.isLowerValueBetter
        ? (a.y ?? -1) - (b.y ?? -1)
        : (b.y ?? -1) - (a.y ?? -1)
      return result
    })
  }

  if (formatterContext.points) {
    const values: number[] = []
    formatterContext.points.forEach((point) => {
      // A holder count is not a dollar. Summing it into the total would move
      // the figure by a rounding error and make it mean nothing.
      if (isOverlayPoint(point)) return

      const isPercentageShare =
        // @ts-expect-error
        point.series.userOptions.stacking === SERIE_STACK.PERCENTAGE

      values.push(isPercentageShare ? point.percentage || 0 : point.y || 0)
    })

    aggregateValue =
      aggregationType === "median" ? median(values) : values.reduce((s, v) => s + v, 0)
  }

  // How many rows at the top are overlays. They sort first, so this is the
  // index of the first ordinary serie — and the point the panel rules off, to
  // show that what follows is the stack and the overlay is not part of it.
  const firstStackIndex =
    formatterContext.points?.findIndex((point) => !isOverlayPoint(point)) ?? -1
  const overlayFieldCount = firstStackIndex > 0 ? firstStackIndex : 0

  const formatted = tooltip.defaultFormatter.call(formatterContext, tooltip)
  const [firstField, ...fields] = formatted

  // Prepare fields array (exclude last empty field)
  const tooltipFields = fields.slice(0, -1)

  // If first field is a string of text and not a timestamp, render as header.
  if (Number.isNaN(new Date(firstField).getDate())) {
    header = tooltipOptions.headerOverride ? tooltipOptions.headerOverride : firstField
  } else {
    const isQuarterGranularity =
      tooltip.chart.series?.[0]?.userOptions?.custom?.granularity === "quarter"

    if (isQuarterGranularity && Number.isInteger(formatterContext.x)) {
      const parsedDate = new Date(formatterContext.x as number)
      const endOfQuarter = new Date(parsedDate)
      endOfQuarter.setMonth(parsedDate.getUTCMonth() + 2)
      const quarter = Math.floor((parsedDate.getUTCMonth() + 3) / 3)

      footerTimestamp = `Q${quarter} - ${parsedDate.toLocaleString("en-US", {
        month: "short",
        timeZone: "UTC",
      })} - ${endOfQuarter.toLocaleString("en-US", {
        month: "short",
        timeZone: "UTC",
      })} ${parsedDate.toLocaleString("en-US", {
        year: "2-digit",
        timeZone: "UTC",
      })}`
    } else {
      footerTimestamp = firstField
    }
  }

  // Calculate aggregation row tooltip if needed
  let totalTooltipHtml: string | undefined
  if (tooltipOptions.showTotal && fields.filter(Boolean).length > 1) {
    // The row borrows a real point to inherit its value formatter, so it has
    // to borrow one that is actually in the total. An overlay sorts first and
    // carries a different unit — taking that one would print a sum of dollars
    // as a bare count.
    const totalTemplate =
      formatterContext.points?.find((point) => !isOverlayPoint(point)) ??
      formatterContext.points?.[0]

    const totalTooltip = (
      tooltip as unknown as {
        bodyFormatter: (items: Array<Point>) => Array<string>
      }
    ).bodyFormatter([
      {
        ...totalTemplate,
        // @ts-expect-error
        point: {
          ...totalTemplate?.point,
          color: null,
          y: aggregateValue,
          percentage: aggregateValue,
          series: {
            tooltipOptions: {},
            name: aggregationLabel,
          },
        },
      },
    ])

    totalTooltipHtml = totalTooltip[0]
  }

  // Render React component to static markup
  return ReactDOMServer.renderToStaticMarkup(
    <ChartTooltip
      header={header}
      footerTimestamp={footerTimestamp}
      fields={tooltipFields}
      overlayFieldCount={overlayFieldCount}
      showTotal={tooltipOptions.showTotal}
      totalTooltip={totalTooltipHtml}
    />,
  )
}
